import { useEffect, useRef, useState } from 'react';
import { initializePaddle, type Paddle, type PaddleEventData } from '@paddle/paddle-js';
import { useTranslation } from '../../../i18n/useTranslation';
import { AuthService } from '../../../Services/AuthService';
import { GeoService } from '../../../Services/GeoService';
import { SubscriptionService } from '../../../Services/SubscriptionService';
import type { UserModel } from '../../../Models/UserModel';
import { PREMIUM_FEATURE_KEYS, type BillingInterval } from '../../../Utils/pricingTiers';
import { Spinner } from '../Spinner/Spinner';
import './PricingTiers.css';

/**
 * Paddle.js is loaded once per session and shared by every caller of this
 * component: most sessions never open a pricing screen, so the SDK is
 * fetched on demand rather than bundled into every page load.
 */
let paddlePromise: Promise<Paddle | undefined> | null = null;
function loadPaddle(token: string, environment: 'sandbox' | 'production'): Promise<Paddle | undefined> {
  paddlePromise ??= initializePaddle({ token, environment });
  return paddlePromise;
}

interface ResolvedPlan {
  paddle: Paddle;
  priceId: { monthly: string | null; annual: string | null };
  monthlyTotal: string | null;
  annualTotal: string | null;
}

const DISPLAY_PRICES = {
  he: {
    month: { primary: '29.90 ₪', alternate: '$9.90' },
    year: { primary: '289.90 ₪', alternate: '$99.90' }
  },
  en: {
    month: { primary: '$9.90', alternate: '₪29.90' },
    year: { primary: '$99.90', alternate: '₪289.90' }
  }
} as const;

/**
 * The single Premium plan, priced live from Paddle. Both the public pricing
 * page and the in-app upgrade screen render this; only where a completed
 * checkout sends the browser next differs (onPurchased vs successPath).
 */
export function PricingTiers({
  onPurchased,
  successPath
}: {
  /** In-app upgrade: re-read the subscription in place once checkout completes. */
  onPurchased?: () => void;
  /** Marketing page: where Paddle sends the browser after a completed checkout. */
  successPath?: string;
}) {
  const { tr, language } = useTranslation();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month');
  const [resolved, setResolved] = useState<ResolvedPlan | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [authUser, setAuthUser] = useState<UserModel | null | undefined>(undefined);
  const onPurchasedRef = useRef(onPurchased);
  onPurchasedRef.current = onPurchased;

  useEffect(() => AuthService.onChange(setAuthUser), []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const config = await SubscriptionService.paddleConfig();
        const monthlyPriceId = config.prices.monthly ?? null;
        const annualPriceId = config.prices.annual ?? null;
        if (!config.enabled || !config.clientToken || (!monthlyPriceId && !annualPriceId)) {
          if (!cancelled) setResolved(null);
          return;
        }

        const paddle = await loadPaddle(config.clientToken, config.environment);
        if (!paddle) throw new Error('Paddle did not load');

        paddle.Update({
          eventCallback: (event: PaddleEventData) => {
            if (event?.name === 'checkout.completed') onPurchasedRef.current?.();
          }
        });

        // Best effort only: an unknown or failed lookup omits the address, and
        // PricePreview falls back to detecting the visitor's own IP.
        const country = await GeoService.country().then(r => r.country).catch(() => null);

        const items = [monthlyPriceId, annualPriceId]
          .filter((id): id is string => id !== null)
          .map(priceId => ({ priceId, quantity: 1 }));
        const preview = await paddle.PricePreview({
          items,
          ...(country ? { address: { countryCode: country } } : {})
        });
        const lineItems = preview.data.details.lineItems;
        let index = 0;
        const monthlyTotal = monthlyPriceId ? (lineItems[index++]?.formattedTotals.total ?? null) : null;
        const annualTotal = annualPriceId ? (lineItems[index++]?.formattedTotals.total ?? null) : null;

        if (!cancelled) {
          setResolved({ paddle, priceId: { monthly: monthlyPriceId, annual: annualPriceId }, monthlyTotal, annualTotal });
          if (!monthlyPriceId) setBillingInterval('year');
        }
      } catch {
        if (!cancelled) setResolved(null);
      }
    }

    void run();
    return () => { cancelled = true; };
    // Prices are fetched once per mount; the language a description renders
    // in does not change which Paddle prices exist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The webhook only ever activates a subscription it can tie to a Firebase
   * uid (via `customData.uid`, read in paddle-service.ts's `extractUid`), so
   * a visitor who is not signed in is signed in first, right here, rather
   * than opening a checkout that could never actually grant anything. This
   * matters most on the public /pricing page, which renders outside AuthGate
   * and so has no session of its own to fall back on.
   */
  async function subscribe() {
    if (!resolved || authUser === undefined) return;
    const priceId = billingInterval === 'month' ? resolved.priceId.monthly : resolved.priceId.annual;
    if (!priceId) return;
    setBusy(true);
    setError(false);
    try {
      const user = authUser ?? await AuthService.signInWithGoogle();
      if (!user) return; // A closed popup is a decision, not a failure.
      setAuthUser(user);

      resolved.paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        ...(user.email ? { customer: { email: user.email } } : {}),
        customData: { uid: user.uid },
        settings: {
          displayMode: 'overlay',
          variant: 'one-page',
          locale: language === 'he' ? 'he' : 'en',
          theme: 'light',
          ...(successPath ? { successUrl: `${window.location.origin}${successPath}` } : {})
        }
      });
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const hasBothIntervals = resolved === undefined || resolved === null
    || Boolean(resolved.priceId.monthly && resolved.priceId.annual);
  const previewTotal = resolved
    ? (billingInterval === 'month' ? resolved.monthlyTotal : resolved.annualTotal)
    : null;
  const displayPrice = DISPLAY_PRICES[language][billingInterval];
  const total = previewTotal ?? displayPrice.primary;
  const alternatePrice = previewTotal && /₪|ILS/i.test(previewTotal)
    ? DISPLAY_PRICES.he[billingInterval].alternate
    : previewTotal && /\$|USD/i.test(previewTotal)
      ? DISPLAY_PRICES.en[billingInterval].alternate
      : displayPrice.alternate;
  const checkoutAvailable = Boolean(resolved && previewTotal);

  return (
    <section className="pricing-tiers pricing-plan pricing-plan-premium">
      <div className="pricing-plan-heading">
        <p className="pricing-tier-badge">{tr('pricingPremiumBadge')}</p>
        <h2>{tr('premiumTitle')}</h2>

        {hasBothIntervals && (
          <fieldset className="pricing-billing-toggle">
            <legend className="visually-hidden">{tr('pricingBillingLabel')}</legend>
            <div className="pricing-billing-options">
              <button
                type="button"
                className={billingInterval === 'month' ? 'is-selected' : ''}
                aria-pressed={billingInterval === 'month'}
                onClick={() => setBillingInterval('month')}
              >
                {tr('pricingBillingMonthly')}
              </button>
              <button
                type="button"
                className={billingInterval === 'year' ? 'is-selected' : ''}
                aria-pressed={billingInterval === 'year'}
                aria-label={tr('pricingBillingYearly')}
                onClick={() => setBillingInterval('year')}
              >
                {tr('pricingBillingYearly')}
                <span>{tr('pricingAnnualSaving')}</span>
              </button>
            </div>
          </fieldset>
        )}

        <p className="pricing-tier-price">
          <bdi>{total}</bdi>
          <span>{tr(billingInterval === 'month' ? 'pricingPerMonth' : 'pricingPerYear')}</span>
        </p>
        <p className="pricing-tier-alternate">
          {tr('pricingAlternatePrefix')} <bdi>{alternatePrice}</bdi>
        </p>
        <p className="pricing-plan-sub">{tr('planPremiumSub')}</p>
      </div>

      <ul className="pricing-feature-list">
        {PREMIUM_FEATURE_KEYS.map(key => (
          <li key={key}><CheckIcon /> <span>{tr(key)}</span></li>
        ))}
      </ul>

      <button type="button" className="primary-button" onClick={() => void subscribe()} disabled={busy || !checkoutAvailable || authUser === undefined}>
        {busy ? <Spinner inline /> : tr('pricingSubscribeCta')}
      </button>

      <p className="pricing-trial-note">{tr('pricingTrialNote')}</p>
      {resolved === null && <p className="pricing-unavailable">{tr('pricingCheckoutUnavailable')}</p>}

      {error && <p className="pricing-tier-error" role="alert">{tr('checkoutFailed')}</p>}
    </section>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m5 10.5 3.1 3.1L15 6.8" />
    </svg>
  );
}
